import { Loading } from '@livecontrol/core-ui';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { SelectOption } from '../../components';
import { Select, SmartSearch } from '../../components';
import type { Device } from '../../model';
import { Store } from '../../store';
import { matchesCategory, matchesSearch, sortDevices } from './device-filters';
import { Devices } from './devices';
import { Filters } from './filters';
import type { FilterKey } from './filters-config';
import { useDevices, useInstallers } from './graphql';
import { UnlinkModal, UpdateModal } from './modal';
import { Pagination } from './pagination';
import { StatCards } from './stat-cards';
import { latestVersion } from './version';

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  > * {
    flex-shrink: 0;
  }
`;

const SearchBox = styled.div`
  width: 250px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: var(--white60);

  .item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .sq {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }

  .sq.current {
    background: rgba(53, 211, 153, 0.16);
    border: 1px solid rgb(53, 211, 153);
  }

  .sq.update {
    background: rgba(242, 179, 61, 0.16);
    border: 1px solid rgb(242, 179, 61);
  }
`;

const Count = styled.div`
  font-size: 12px;
  color: var(--white60);
  white-space: nowrap;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding: 12px 18px;
  border-radius: 12px;
  background: rgba(17, 27, 51, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);

  .count {
    font-size: 14px;
    color: var(--white);
  }

  .count b {
    font-weight: 700;
    color: rgb(140, 150, 255);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const BarButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--white);
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.06);
  }
`;

// Values map to the client-side sort handled by sortDevices.
const SORT_OPTIONS: SelectOption<string>[] = [
  { value: '', label: 'No sorting' },
  { value: 'client_id', label: 'Sort by CID' },
  { value: 'lcs_version', label: 'Sort by LCS' },
  { value: 'ems_version', label: 'Sort by EMS' }
];

const PAGE_SIZE = 15;

export const Manager = (): ReactElement => {
  const { me } = Store.User.useIdentity();
  const hasUpdatePermissions = !!me?.commandCenterAccess?.installer_access;
  const [filterKey, setFilterKey] = useState<FilterKey>('all');
  const [sortField, setSortField] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Device[]>([]);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(undefined);

  const { devices: allDevices, loading, refetch } = useDevices();
  const { installers } = useInstallers({ pagination: { limit: 0 } });

  const latestLcs = useMemo(() => latestVersion(installers, 'LCS'), [installers]);
  const latestEms = useMemo(() => latestVersion(installers, 'EMS'), [installers]);

  const searched = useMemo(
    () => (allDevices ?? []).filter((device) => matchesSearch(device, search)),
    [allDevices, search]
  );

  // Per-category counts for the stat cards, over the search-filtered set.
  const counts = useMemo<Record<FilterKey, number>>(
    () => ({
      all: searched.length,
      online: searched.filter((device) => matchesCategory(device, 'online')).length,
      offline: searched.filter((device) => matchesCategory(device, 'offline')).length,
      updates: searched.filter((device) => matchesCategory(device, 'updates', latestLcs, latestEms))
        .length,
      speed: searched.filter((device) => matchesCategory(device, 'speed')).length
    }),
    [searched, latestLcs, latestEms]
  );

  const filtered = useMemo(
    () =>
      sortDevices(
        searched.filter((device) => matchesCategory(device, filterKey, latestLcs, latestEms)),
        sortField
      ),
    [searched, filterKey, sortField, latestLcs, latestEms]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const visibleDevices = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  // Selecting a card or a dropdown option both flow through here, keeping them in sync.
  const applyFilter = (key: FilterKey): void => {
    setFilterKey(key);
    setPage(1);
  };

  const applySort = (field: string): void => {
    setSortField(field);
    setPage(1);
  };

  const handleUnlinkAttempt = (aSelectedDevice: Device): void => {
    setSelectedDevice(aSelectedDevice);
    setShowUnlinkModal(true);
  };

  const handleDevicesRefetch = (): void => {
    void refetch();
  };

  return (
    <div>
      <TopRow>
        <StatCards value={filterKey} counts={counts} onChange={applyFilter} />

        <FilterGroup>
          <Filters value={filterKey} onChange={applyFilter} />
          <Select icon='fas fa-sort' value={sortField} options={SORT_OPTIONS} onChange={applySort} />
          <SearchBox>
            <SmartSearch
              search={search}
              onSearch={(search_): void => {
                setSearch(search_);
                setPage(1);
              }}
            />
          </SearchBox>
        </FilterGroup>
      </TopRow>

      <LegendRow>
        <Legend>
          <span className='item'>
            <span className='sq current' /> Software current
          </span>
          <span className='item'>
            <span className='sq update' /> Update available
          </span>
        </Legend>
        <Count>
          {visibleDevices.length} of {filtered.length} devices
        </Count>
      </LegendRow>

      {selected.length > 0 && (
        <SelectionBar>
          <span className='count'>
            <b>{selected.length}</b> selected
          </span>
          <div className='actions'>
            <BarButton type='button'>Notify clients</BarButton>
            {hasUpdatePermissions && (
              <BarButton
                type='button'
                onClick={(): void => {
                  setModal(true);
                }}
              >
                Update software
              </BarButton>
            )}
          </div>
        </SelectionBar>
      )}

      {loading ? (
        <Loading isOldVersion />
      ) : visibleDevices.length ? (
        <>
          <Devices
            devices={visibleDevices}
            updateEnabled={hasUpdatePermissions}
            latestLcs={latestLcs}
            latestEms={latestEms}
            selectDevices={setSelected}
            showUpdate={setModal}
            unlinkAttempt={handleUnlinkAttempt}
          />
          <Pagination
            page={currentPage}
            changePage={setPage}
            totalPages={totalPages}
            loading={loading}
          />
        </>
      ) : (
        <p className='text-white text-center'>No Devices</p>
      )}

      {installers && modal && (
        <UpdateModal
          selectedDevices={selected}
          installers={installers}
          show={modal}
          onClose={(): void => {
            setModal(false);
            setSelected([]);
          }}
        />
      )}
      {selectedDevice && showUnlinkModal && (
        <UnlinkModal
          handleDevicesRefetch={handleDevicesRefetch}
          selectedDevice={selectedDevice}
          show={showUnlinkModal}
          onClose={(): void => {
            setSelectedDevice(undefined);
            setShowUnlinkModal(false);
          }}
        />
      )}
    </div>
  );
};
